import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Navigate, useParams } from "react-router-dom";
import { CompanyCardInfoStack } from "../components/company/card/CompanyCardInfoStack";
import { CompanyEventTotalsBlock } from "../components/company/card/CompanyEventTotalsBlock";
import { CompanyCardSoftSection } from "../components/company/card/CompanyCardSoftSection";
import PartnerLinkedCompaniesPanel from "../components/company/PartnerLinkedCompaniesPanel";
import ApiData from "../api/ApiData";
import Memory from "../api/Memory";
import Loader from "../components/Loader";
import { resolveDirectionFromStorage } from "../utils/softVersionCompareUtils";
import { observer } from "mobx-react-lite";
import RolesState from "../api/RolesState";
import RegionBtn from "../components/company/RegionBtn";
import { CompanyEditForm } from "../components/company/CompanyEditForm";
import User from "../api/User";
import { BtnOutlineV4 } from "../components/UI/button/BtnOutlineV4";
import { ReqItem } from "../components/UI/ReqItem";
import { CompanyClientLkStatusReqItem } from "../components/company/card/CompanyClientLkStatusReqItem";
import { CompanyClientPartnersBlock } from "../components/company/card/CompanyClientPartnersBlock";
import { ContainerSubTitleGrey } from "../components/ContainerSubTitleGrey";

const formatManagerName = (manager) => {
  if (manager === null || manager === undefined) {
    return { text: "менеджер не заполнен", muted: true };
  }
  const name = [manager.first_name, manager.last_name, manager.father_name]
    .filter(Boolean)
    .join(" ");
  if (!name) {
    return { text: "менеджер не заполнен", muted: true };
  }
  return { text: name, muted: false };
};

const innLabelByCountry = (country) => {
  if (country === "BY") return "УНП";
  if (country === "KZ") return "БИН";
  return "ИНН";
};

const CLIENT_TOP_LAYOUT_MIN_WIDTH = 1101;

const PartnerCard = observer(() => {
  const refSoftCompany = useRef(null);
  const infoColumnRef = useRef(null);
  const [clientTopInfoHeight, setClientTopInfoHeight] = useState(null);
  const { inn } = useParams();
  const [nameCompany, setNameCompany] = useState(null);
  const [kpp, setKpp] = useState(null);
  const [companyAddress, setCompanyAddress] = useState(null);
  const [descriptions, setDescriptions] = useState(null);
  const [idSearch, setIdSearch] = useState("");
  const [edit, setEdit] = useState(false);
  const [isPartner, setIsPartner] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [country, setCountry] = useState("RU");
  const [versionFilterOn, setVersionFilterOn] = useState(
    sessionStorage.getItem("soft_version") ? true : false,
  );
  const [partnerMismatch, setPartnerMismatch] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("condition_version_client")) {
      const item = resolveDirectionFromStorage();
      Memory.setVersionReduction(item.memory);
    }
  }, []);

  useEffect(() => {
    if (ApiData.company.company !== null) {
      setKpp(ApiData.company.company.kpp);
      setDescriptions(ApiData.company.company.descriptions);
      setNameCompany(ApiData.company.company.name_company);
      setCompanyAddress(ApiData.company.company.address_company);
      setIsPartner(!!ApiData.company.company.is_partner);
      setIsActive(ApiData.company.company.is_active !== false);
      setPartnerMismatch(!ApiData.company.company.is_partner);
    }
  }, [ApiData.company.company]);

  const [nav, setNav] = useState([
    { nameRu: "Софт", name: "soft", active: true, allowed: true },
    {
      nameRu: "Аналитика",
      name: "analitic",
      active: false,
      allowed: RolesState.all_soft_client || User.user.is_superuser,
    },
    {
      nameRu: "Инцидент",
      name: "incident",
      active: false,
      allowed: RolesState.all_soft_client || User.user.is_superuser,
    },
    {
      nameRu: "Сотрудники",
      name: "employees",
      active: false,
      allowed:
        RolesState.get_client_lk_users || User.user?.is_superuser,
    },
  ]);

  const clickNav = (e) => {
    setNav((itemNav) =>
      itemNav.map((n) => ({
        ...n,
        active: e.name === n.name,
      })),
    );
  };

  const getDataFunc = async () => {
    const res = await ApiData.getDataCompanyCard(inn);
    if (res) {
      setCountry(
        !ApiData.company.company?.country_id_rel?.tag_country
          ? "RU"
          : ApiData.company.company?.country_id_rel?.tag_country,
      );
    }
  };

  useEffect(() => {
    ApiData.getListCountry();
    if (ApiData.companys === null) {
      ApiData.getDataCompanys();
    }
    getDataFunc();
  }, [inn]);

  useLayoutEffect(() => {
    const infoEl = infoColumnRef.current;
    if (!infoEl || edit) {
      setClientTopInfoHeight(null);
      return undefined;
    }
    const syncSitesPanelHeight = () => {
      if (window.innerWidth < CLIENT_TOP_LAYOUT_MIN_WIDTH) {
        setClientTopInfoHeight(null);
        return;
      }
      setClientTopInfoHeight(infoEl.offsetHeight);
    };
    syncSitesPanelHeight();
    const resizeObserver = new ResizeObserver(syncSitesPanelHeight);
    resizeObserver.observe(infoEl);
    window.addEventListener("resize", syncSitesPanelHeight);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", syncSitesPanelHeight);
    };
  }, [edit, nameCompany, kpp, companyAddress, descriptions]);

  const showEditor = () => {
    setEdit((v) => !v);
  };

  const editCompany = async () => {
    const dataCompany = { id: ApiData.company.company.id };
    if (nameCompany !== "") {
      dataCompany.name_company = nameCompany;
      ApiData.setParametersCompany("name_company", nameCompany);
    }
    if (kpp !== "") {
      dataCompany.kpp = kpp;
      ApiData.setParametersCompany("kpp", kpp);
    }
    if (companyAddress !== "") {
      dataCompany.address_company = companyAddress;
      ApiData.setParametersCompany("address_company", companyAddress);
    }
    if (descriptions !== "") {
      dataCompany.descriptions = descriptions;
      ApiData.setParametersCompany("descriptions", descriptions);
    }
    const res = await ApiData.updateCompany(dataCompany);
    if (res) {
      getDataFunc();
    }
    setEdit(false);
  };

  const updateCountry = async (e) => {
    const countryVslue = JSON.parse(e.target.value);
    await ApiData.updateCompany({ country_id: Number(countryVslue.id) });
    setCountry(countryVslue.tag_country);
  };

  const handleManagerSelect = async (user) => {
    if (!user?.id || !ApiData.company.company?.id) return;
    await ApiData.assignManager(ApiData.company.company.id, user.id);
  };

  const handleSupportManagerSelect = async (user) => {
    if (!user?.id || !ApiData.company.company?.id) return;
    const ok = await ApiData.assignSupportManager(
      ApiData.company.company.id,
      user.id,
    );
    if (ok) {
      const supportManager = ApiData.company.company?.support_manager;
      ApiData.syncCompanyInLists(ApiData.company.company.id, {
        support_manager: supportManager,
      });
    }
  };

  const handlePartnerChange = async () => {
    const next = !isPartner;
    const res = await ApiData.updateCompany({ is_partner: next });
    if (res) {
      ApiData.setParametersCompany("is_partner", next);
      ApiData.syncCompanyInLists(ApiData.company.company.id, {
        is_partner: next,
      });
      setIsPartner(next);
    }
  };

  const handleArchiveChange = async () => {
    const nextIsActive = !isActive;
    const res = await ApiData.updateCompany({ is_active: nextIsActive });
    if (res) {
      ApiData.setParametersCompany("is_active", nextIsActive);
      ApiData.syncCompanyInLists(ApiData.company.company.id, {
        is_active: nextIsActive,
      });
      setIsActive(nextIsActive);
    }
  };

  const managerDisplay = formatManagerName(ApiData.company.company?.manager);
  const supportManagerDisplay = formatManagerName(
    ApiData.company.company?.support_manager,
  );

  if (!RolesState.company_partners) {
    return <Navigate to="/noright" />;
  }

  if (partnerMismatch && ApiData.company.company) {
    return (
      <div className="main-page company-card-v4">
        <ContainerSubTitleGrey
          text="Карточка доступна только для организаций с типом «Партнёр». Откройте запись из раздела «Партнёры»."
        />
      </div>
    );
  }

  return (
    <div className="main-page">
      {ApiData.company.company !== null ? (
        <div className="company-card-v4">
          {edit ? (
            <CompanyEditForm
              inn={inn}
              innLabel={innLabelByCountry(country)}
              country={country}
              kpp={kpp}
              nameCompany={nameCompany}
              companyAddress={companyAddress}
              descriptions={descriptions}
              managerDisplay={managerDisplay}
              supportManagerDisplay={supportManagerDisplay}
              isPartner={isPartner}
              isActive={isActive}
              setKpp={setKpp}
              setNameCompany={setNameCompany}
              setCompanyAddress={setCompanyAddress}
              setDescriptions={setDescriptions}
              onCancel={showEditor}
              onSave={editCompany}
              onCountryChange={updateCountry}
              onManagerSelect={handleManagerSelect}
              onSupportManagerSelect={handleSupportManagerSelect}
              onPartnerChange={handlePartnerChange}
              onArchiveChange={handleArchiveChange}
            />
          ) : (
            <>
              <section
                className="client-top-layout"
                style={
                  clientTopInfoHeight
                    ? {
                        "--client-top-info-height": `${clientTopInfoHeight}px`,
                      }
                    : undefined
                }
              >
                <div className="info-column" ref={infoColumnRef}>
                  <header className="card header-card">
                    <div className="header-top">
                      <div className="company-title">
                        <div className="compamy-title-name-container">
                          <span className="req-label">Партнёр</span>
                          <h1>{ApiData.company.company.name_company}</h1>
                          {ApiData.company.company.is_active === false ? (
                            <span
                              className="req-label"
                              style={{ color: "var(--error)" }}
                            >
                              В архиве
                            </span>
                          ) : null}
                        </div>
                        <RegionBtn region={country} badgeV4 />
                      </div>
                      {RolesState.patch_company ? (
                        <BtnOutlineV4 onClick={showEditor} text="Редактировать" />
                      ) : null}
                    </div>
                    <div className="requisites-grid">
                      <ReqItem
                        label={`${innLabelByCountry(country)} / КПП`}
                        value={`${inn} / ${kpp || "—"}`}
                        mono
                      />
                      <ReqItem label="Адрес" value={companyAddress || "—"} />
                      <ReqItem
                        className="manager-field"
                        label="Ответственный менеджер"
                        value={managerDisplay.text}
                        muted={managerDisplay.muted}
                      />
                      <ReqItem
                        className="manager-field"
                        label="Менеджер сопровождения"
                        value={supportManagerDisplay.text}
                        muted={supportManagerDisplay.muted}
                      />
                      <CompanyClientPartnersBlock
                        companyId={ApiData.company.company?.id}
                      />
                      <CompanyClientLkStatusReqItem />
                    </div>
                    <p className="description-text">
                      {descriptions !== "[NULL]" && descriptions
                        ? descriptions
                        : "Описания нет"}
                    </p>
                  </header>
                  <CompanyCardInfoStack />
                  <CompanyEventTotalsBlock
                    companyId={ApiData.company.company?.id}
                  />
                </div>
                <div className="sites-column">
                  <PartnerLinkedCompaniesPanel
                    partnerCompanyId={ApiData.company.company.id}
                  />
                </div>
              </section>
              {RolesState.soft_company_id ? (
                <CompanyCardSoftSection
                  nav={nav}
                  clickNav={clickNav}
                  companyId={ApiData.company.company?.id}
                  versionFilterOn={versionFilterOn}
                  setVersionFilterOn={setVersionFilterOn}
                  setIdSearch={setIdSearch}
                  idSearch={idSearch}
                  refSoftCompany={refSoftCompany}
                  showNoProductionFilter={false}
                />
              ) : null}
            </>
          )}
        </div>
      ) : (
        <Loader />
      )}
    </div>
  );
});

export default PartnerCard;
